import React, {
  useReducer,
  useMemo,
  useCallback,
  ReactNode,
  ComponentProps,
} from 'react';

import PropTypes from 'prop-types';

import {
  CONTEXT_NAMES,
  getOrCreateGlobalContext,
  useContextWithValidation,
} from '@adsk/alloy-react-context';
import theme, { StyleProp, stylePropType } from '@adsk/alloy-react-theme';

import Notification from '../Notification';

const SHOW_NOTIFICATION = 'SHOW_NOTIFICATION';
const REMOVE_NOTIFICATION = 'REMOVE_NOTIFICATION';
const NOTIFICATION_DISMISS_TIMEOUT = 6;

type Notif = Omit<ComponentProps<typeof Notification>, 'children'> & {
  message?: ReactNode;
};

type ID = string | number;

type Action = {
  id?: ID;
  type: typeof SHOW_NOTIFICATION | typeof REMOVE_NOTIFICATION;
  payload?: Notif;
};

function notificationReducer(notifications: Notif[], action: Action): Notif[] {
  switch (action.type) {
    case REMOVE_NOTIFICATION:
      return notifications.filter(
        (notification) => notification.id !== action.id,
      );
    case SHOW_NOTIFICATION:
      return [...notifications, ...(action.payload ? [action.payload] : [])];
    default:
      console.error(
        `NotificationsProvider - Unknown action type: ${action.type}`,
      );
      return notifications;
  }
}

const generateRandomID = () => Math.random().toString(36).substring(2, 15);

const NotificationsContext = getOrCreateGlobalContext<NotificationsContextType>(
  CONTEXT_NAMES.NotificationsContext,
);

export type NotificationsContextType = {
  showNotification: (notification: Notif) => {
    id: string;
    removeNotification: () => void;
  };
  removeNotification: (id: string | number) => void;
};
export const useNotifications = (): NotificationsContextType =>
  useContextWithValidation<NotificationsContextType>(NotificationsContext, {
    hook: 'useNotifications',
    provider: 'NotificationsProvider',
  });

type NotificationsProviderProps = {
  style?: StyleProp;
  children?: ReactNode;
};

export function NotificationsProvider({
  style,
  children,
}: NotificationsProviderProps) {
  const [notifications, dispatch] = useReducer(notificationReducer, []);

  const removeNotification = useCallback(
    (id?: ID) => dispatch({ type: REMOVE_NOTIFICATION, id }),
    [],
  );

  const showNotification = useCallback(
    (notification: Notif) => {
      let timeout: ReturnType<typeof setTimeout>;
      const id = notification.id || generateRandomID();

      const dismissWrapper = () => {
        removeNotification(id);
        clearTimeout(timeout);

        if (
          notification.onDismiss &&
          typeof notification.onDismiss === 'function'
        ) {
          notification.onDismiss();
        }
      };
      const hasPromise =
        notification.asyncOperation &&
        typeof notification.asyncOperation.then === 'function';
      const finalNotification = {
        autoDismiss: NOTIFICATION_DISMISS_TIMEOUT,
        dismissible: !hasPromise,
        ...notification,
        id,
        onDismiss: dismissWrapper,
      };
      const configureTimeOut = () =>
        setTimeout(
          finalNotification.onDismiss,
          finalNotification.autoDismiss * 1000,
        );
      dispatch({ type: SHOW_NOTIFICATION, payload: finalNotification });
      if (hasPromise) {
        finalNotification.asyncOperation &&
          finalNotification.asyncOperation
            .then(() => {
              timeout = configureTimeOut();
            })
            .catch(() => {
              timeout = configureTimeOut();
            });
      } else if (
        finalNotification.dismissible !== false &&
        finalNotification.autoDismiss > 0
      ) {
        timeout = configureTimeOut();
      }

      return { id, removeNotification: () => removeNotification(id) };
    },
    [removeNotification],
  );

  const contextValue = useMemo<NotificationsContextType>(
    () => ({ showNotification, removeNotification }),
    [showNotification, removeNotification],
  );

  return (
    <NotificationsContext.Provider value={contextValue}>
      {children}
      <div
        css={[
          {
            position: 'fixed',
            zIndex: theme.zIndex.notification,
            right: 16,
            top: 16 + 72 /* header height is 72 */,
          },
          style,
        ]}
      >
        {notifications.map((notification) => {
          const {
            message,
            id,
            // this is unused in the Notification component
            autoDismiss: _ignoredAutoDismiss,
            style: notificationStyle,
            ...rest
          } = notification;

          return (
            <Notification
              {...rest}
              key={id}
              style={[{ marginBottom: theme.spacing.XS }, notificationStyle]}
            >
              {message}
            </Notification>
          );
        })}
      </div>
    </NotificationsContext.Provider>
  );
}

NotificationsProvider.propTypes = {
  style: stylePropType,
  children: PropTypes.any,
};
